import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { has } from 'lodash';
import { AUTH_PATHS, RecoveryPassword } from '../auth';
import { AuthService } from '../auth.service';
import { Enums } from '../../../configs/Enums';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { Breadcrumb } from '../../../shared/breadcrumbs/breadcrumbs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  standalone: false,
})
export class ChangePasswordComponent implements OnInit {
  public AUTH_PATHS = AUTH_PATHS;
  public token: string;
  public recoveryError: string | null;
  public beforePasswordError: boolean;
  public recoveryChangeError: boolean;
  public rAccessChangePasswordBreadcrumbs: Breadcrumb[] = [{ text: 'Восстановление доступа', url: null }];
  public changePasswordForm: UntypedFormGroup = new UntypedFormGroup({
    new_password: new UntypedFormControl('', [Validators.required, Validators.minLength(8)]),
    new_password_confirm: new UntypedFormControl({ value: '', disabled: true }, Validators.required),
    new_password_confirm_hidden: new UntypedFormControl(''),
  });

  public regxps = {
    range: /^[A-Za-z0-9]+$/,
    lowercase: /[a-z]/,
    uppercase: /[A-Z]/,
    numbers: /[\d]/,
  };

  public newPasswordValidation = {
    range: false,
    lowercase: false,
    uppercase: false,
    numbers: false,
    minLength: false,
    allMatch: false,
    passwordStatus: 'Ненадежный пароль',
  };

  @ViewChild('p') popover: NgbPopover;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private breadcrumbsService: BreadcrumbsService
  ) {}

  ngOnInit() {
    this.onChanges();

    this.route.paramMap.subscribe((params) => {
      this.token = params.get('token');

      this.checkValidToken();
    });

    this.breadcrumbsService.updateBreadcrumbs(this.rAccessChangePasswordBreadcrumbs);
  }

  onChanges(): void {
    this.changePasswordForm.get('new_password').valueChanges.subscribe((val) => {
      const validation = this.newPasswordValidation;
      const regxps = this.regxps;

      validation.range = this.checkRegexp(val, regxps.range);
      validation.lowercase = this.checkRegexp(val, regxps.lowercase);
      validation.uppercase = this.checkRegexp(val, regxps.uppercase);
      validation.numbers = this.checkRegexp(val, regxps.numbers);
      validation.minLength = val && val.length > 7;
      validation.allMatch = validation.range && validation.lowercase && validation.uppercase && validation.numbers && validation.minLength;

      if (validation.allMatch) {
        this.closeValidationPopover();
        this.changePasswordForm.controls['new_password_confirm'].enable();
      } else {
        this.openValidationPopover();
        this.changePasswordForm.controls['new_password_confirm'].disable();
      }

      if (this.changePasswordForm.value.new_password !== this.changePasswordForm.value.new_password_confirm) {
        this.changePasswordForm.controls['new_password_confirm_hidden'].setErrors({
          invalid: true,
        });
      } else {
        this.changePasswordForm.controls['new_password_confirm_hidden'].setErrors(null);
        this.changePasswordForm.controls['new_password_confirm'].setErrors(null);
      }

      this.resetErrors();
    });

    this.changePasswordForm.get('new_password_confirm').valueChanges.subscribe((val) => {
      if (val !== this.changePasswordForm.value.new_password) {
        this.changePasswordForm.controls['new_password_confirm'].setErrors({
          invalid: true,
        });
        this.changePasswordForm.controls['new_password_confirm_hidden'].setErrors({
          invalid: true,
        });
      } else {
        this.changePasswordForm.controls['new_password_confirm'].setErrors(null);
        this.changePasswordForm.controls['new_password_confirm_hidden'].setErrors(null);
      }

      this.resetErrors();
    });
  }

  private checkValidToken(): void {
    this.authService.checkValidToken(this.token).subscribe(
      (response) => {
        this.checkResponse(response['metadata'], null);
      },
      (error) => {
        this.checkResponse(error.error['metadata'], null);
      }
    );
  }

  private checkResponse(metadata: any, isChange: boolean | null): void {
    if (has(metadata, 'code')) {
      this.checkCode(metadata.code, isChange);
    } else {
      this.recoveryError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
    }
  }

  private checkCode(code: number, isChange: boolean | null): void {
    switch (code) {
      case 0:
        if (isChange) {
          this.router.navigate(['/' + AUTH_PATHS.change_password_success]);
        }

        return;
      case 100000:
        this.recoveryError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
        return;
      case 100006:
        this.router.navigate(['/' + AUTH_PATHS.change_password_incorrect]);
        return;
      case 100007:
      case 100008:
      case 100009:
        if (code === 100009) {
          this.beforePasswordError = true;
        } else {
          this.recoveryChangeError = true;
        }
        this.recoveryError = Enums.errorCodes[code];
        return;
      default:
        this.recoveryError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
        return;
    }
  }

  public openValidationPopover(): void {
    if (!this.newPasswordValidation.allMatch) {
      this.popover.open();
    }
  }

  public closeValidationPopover(): void {
    this.popover.close();
  }

  public checkRegexp(str: string, regexp: RegExp): boolean {
    return regexp.test(str);
  }

  private resetErrors(): void {
    this.recoveryError = null;
    this.recoveryChangeError = null;
    this.beforePasswordError = null;
  }

  public onSubmitChangePasswordForm(): void {
    const recoveryRequestObj: RecoveryPassword = {
      token: this.token,
      new_password: this.changePasswordForm.value.new_password,
      new_password_confirm: this.changePasswordForm.value.new_password_confirm,
    };

    this.resetErrors();

    this.authService.changeExpiriesPassword(recoveryRequestObj).subscribe(
      (response) => {
        this.checkResponse(response['metadata'], true);
      },
      (error) => {
        this.checkResponse(error.error['metadata'], true);
      }
    );
  }
}
